import React, { useCallback, useEffect, useState } from 'react'
import {
  Card,
  Form,
  InputNumber,
  Button,
  Radio,
  Typography,
  Spin,
  message,
  Space,
  Result
} from 'antd'
import {
  CalculatorOutlined,
  PercentageOutlined,
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SaveOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'
import Hint from '../components/common/Hint'

const { Title, Paragraph, Text } = Typography

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 32px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const ContentWrapper = styled.div`
  max-width: 780px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledCard = styled(Card)`
  border-radius: 18px;
  box-shadow: 0 18px 40px rgba(213, 114, 138, 0.12);
  border: 1px solid rgba(213, 114, 138, 0.08);

  .ant-card-head {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  span {
    color: #8c8c8c;
    font-size: 14px;
  }
`

const FormWrapper = styled(Form)`
  margin-top: 16px;

  .ant-form-item {
    margin-bottom: 20px;
  }
`

const TierRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: #fff5f7;
  border: 1px solid #f4d6de;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
`

const LabelText = styled.span`
  color: #576079;
  font-weight: 600;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`

const InfoCard = styled(Card)`
  border-radius: 16px;
  background: #ffffffcc;
  border: 1px dashed #f0ccd4;
`

const MIN_TIERS = 1

const RuleRadioGroup = styled(Radio.Group)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  .ant-radio-wrapper {
    margin: 0;
    padding: 16px;
    border: 1px solid #eef0f5;
    border-radius: 12px;
    background: #fff;
    transition: all 0.18s ease;
    align-items: flex-start;
    width: 100%;
  }
  .ant-radio-wrapper:hover { border-color: #d5728a; background: #fffafb; }
  .ant-radio-wrapper-checked { border-color: #d5728a; background: #fff5f7; box-shadow: 0 4px 12px rgba(213,114,138,0.12); }
  .ant-radio { margin-top: 2px; }
`

const RuleLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 4px;
  .rule-name { font-weight: 600; font-size: 14px; display: flex; align-items: center; gap: 6px; }
  .rule-formula { font-size: 13px; color: #6b7280; font-family: monospace; }
  .rule-desc { font-size: 12px; color: #9ca3af; line-height: 1.6; }
`

const normalizeTiers = (payload) => {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.tiers)) return payload.tiers
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.records)) return payload.records
  if (payload?.threshold !== undefined || payload?.rate !== undefined) {
    return [payload]
  }
  return []
}

const ValueAddedCommissionSettingsPage = () => {
  const { user, businessPlan } = useAuth()
  const navigate = useNavigate()
  const { hasFeature, permissionsLoaded } = useMembership()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [commissionRule, setCommissionRule] = useState('sales_rate')

  const businessId = user?.businessId

  const fetchCommissionSettings = useCallback(async () => {
    if (!businessId) return
    try {
      setLoading(true)
      const response = await api.get(
        `/api/businesses/${businessId}/value-added/commission`
      )
      const payload =
        response.data?.data || response.data?.commission || response.data || []
      const tiers = normalizeTiers(payload)
      const rule = payload?.commissionRule === 'profit_rate_minus_fee'
        ? 'profit_rate_minus_fee'
        : 'sales_rate'
      setCommissionRule(rule)

      if (tiers.length > 0) {
        form.setFieldsValue({
          tiers: tiers.map((tier) => ({
            threshold: Number(tier.threshold ?? tier.minAmount ?? 0),
            rate: Number(tier.rate ?? tier.percentage ?? tier.percent ?? 0)
          }))
        })
      } else {
        form.setFieldsValue({
          tiers: [{ threshold: 0, rate: 0 }]
        })
      }
    } catch (error) {
      console.error('取得儲值抽成設定失敗', error)
      form.setFieldsValue({
        tiers: [{ threshold: 0, rate: 0 }]
      })
    } finally {
      setLoading(false)
    }
  }, [businessId, form])

  useEffect(() => {
    if (!hasFeature('storedValue')) return
    fetchCommissionSettings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, hasFeature])

  // 權限檢查
  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size='large' tip='正在載入權限...' />
        </div>
      </PageWrapper>
    )
  }

  if (!hasFeature('storedValue')) {
    return (
      <PageWrapper>
        <ContentWrapper>
          <Result
            status='403'
            title='功能限制'
            subTitle='此功能需要更高級別的會員方案才能使用，請升級您的會員方案。'
            extra={
              <Button
                type='primary'
                onClick={() => navigate('/settings/membership')}
              >
                查看會員方案
              </Button>
            }
          />
        </ContentWrapper>
      </PageWrapper>
    )
  }

  const handleReset = () => {
    form.resetFields()
    form.setFieldsValue({ tiers: [{ threshold: 0, rate: 0 }] })
    setCommissionRule('sales_rate')
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (!businessId) {
        message.error('未能取得商家資訊，請重新登入後再試')
        return
      }
      if (!values.tiers || values.tiers.length < MIN_TIERS) {
        message.error('請至少新增一組儲值抽成設定')
        return
      }

      setSaving(true)
      await api.put(`/api/businesses/${businessId}/value-added/commission`, {
        commissionRule,
        tiers: values.tiers.map((tier) => ({
          threshold: Number(tier.threshold) || 0,
          rate: Number(tier.rate) || 0
        }))
      })
      message.success('儲值抽成設定已更新')
    } catch (error) {
      if (error?.errorFields) {
        return
      }
      console.error('更新儲值抽成設定失敗', error)
      message.error(
        error.response?.data?.message ||
          error.message ||
          '更新儲值抽成設定失敗，請稍後再試'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <StyledCard
          title={
            <Header>
              <Title level={3} style={{ marginBottom: 0, color: '#0f2562' }}>
                儲值抽成設定
              </Title>
              <span>
                依照儲值金銷售累積金額設定抽成比例，協助掌握儲值業績獎勵。
              </span>
            </Header>
          }
        >
          <Paragraph style={{ color: '#576079', marginBottom: 24 }}>
            可建立多個階梯規則，例如：「累積達 NT$100,000 抽 3%」。
            營業額未達門檻時可維持為 0%，鼓勵同仁提升儲值銷售。
          </Paragraph>

          <FormWrapper
            layout='vertical'
            form={form}
            initialValues={{
              tiers: [{ threshold: 0, rate: 0 }]
            }}
          >
            {/* ── 計算規則 ── */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#1c1c1e', marginBottom: 12 }}>
                計算規則
              </div>
              <RuleRadioGroup
                value={commissionRule}
                onChange={(e) => setCommissionRule(e.target.value)}
              >
                <Radio value='sales_rate'>
                  <RuleLabel>
                    <span className='rule-name'>
                      <PercentageOutlined style={{ color: '#3498db' }} />
                      按業績抽成（不扣手續費）
                    </span>
                    <span className='rule-formula'>抽成 = 儲值金額 × 當階抽成%</span>
                    <span className='rule-desc'>
                      不論支付方式為何（現金、刷卡、轉帳），統一以儲值金額乘以抽成比例計算。最簡單的計算方式。
                    </span>
                  </RuleLabel>
                </Radio>
                <Radio value='profit_rate_minus_fee'>
                  <RuleLabel>
                    <span className='rule-name'>
                      <CalculatorOutlined style={{ color: '#d5728a' }} />
                      手續費由員工吸收（作為薪資減項）
                    </span>
                    <span className='rule-formula'>加項：儲值金額 × 當階抽成%　減項：金流手續費（上限為本筆抽成）</span>
                    <span className='rule-desc'>
                      • 例：$10,000 Line Pay（手續費 2.4%=$240），抽成 2%=$200，減項 $240，員工負擔 $40。<br />
                      • 若手續費 &lt; 抽成：例手續費 $50，抽成 $200，減項 $50，淨得 $150。<br />
                      • 現金、轉帳支付時手續費為 0，結果與規則一相同。<br />
                      • 手續費率在「金流手續費設定」中統一設定。
                    </span>
                  </RuleLabel>
                </Radio>
              </RuleRadioGroup>
            </div>

            {commissionRule === 'profit_rate_minus_fee' && (
              <Hint
                title='提示'
                tone='info'
                style={{ marginBottom: 20, borderRadius: 10 }}
              >
                「扣手續費」規則需要先在「金流手續費設定」中填寫刷卡費率，否則手續費以 0 計算。
              </Hint>
            )}

            <Form.List name='tiers'>
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <TierRow key={field.key}>
                      <Space direction='horizontal' size={12} wrap>
                        <LabelText>累積達到 ≥</LabelText>
                        <Form.Item
                          {...field}
                          name={[field.name, 'threshold']}
                          fieldKey={[field.fieldKey, 'threshold']}
                          rules={[
                            { required: true, message: '請輸入累積金額' },
                            {
                              type: 'number',
                              min: 0,
                              message: '累積金額不得小於 0'
                            }
                          ]}
                        >
                          <InputNumber
                            min={0}
                            placeholder='輸入金額'
                            style={{ width: 180 }}
                            formatter={(value) =>
                              value === undefined || value === null
                                ? ''
                                : `${Number(value)
                                    .toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                            }
                            parser={(value) =>
                              value ? Number(value.replace(/[^\d.]/g, '')) : 0
                            }
                          />
                        </Form.Item>
                      </Space>

                      <Space direction='horizontal' size={12} wrap>
                        <LabelText>抽成趴數</LabelText>
                        <Form.Item
                          {...field}
                          name={[field.name, 'rate']}
                          fieldKey={[field.fieldKey, 'rate']}
                          rules={[
                            { required: true, message: '請輸入抽成趴數' },
                            {
                              type: 'number',
                              min: 0,
                              message: '抽成趴數不得小於 0'
                            }
                          ]}
                        >
                          <InputNumber
                            min={0}
                            max={1000}
                            placeholder='輸入抽成趴數'
                            style={{ width: 160 }}
                            addonAfter='%'
                          />
                        </Form.Item>
                      </Space>

                      {fields.length > MIN_TIERS && (
                        <Button
                          type='text'
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        >
                          移除
                        </Button>
                      )}
                    </TierRow>
                  ))}

                  <Button
                    type='dashed'
                    onClick={() => add({ threshold: 0, rate: 0 })}
                    block
                    icon={<PlusOutlined />}
                    style={{
                      borderStyle: 'dashed',
                      marginTop: 12,
                      height: 48
                    }}
                  >
                    新增儲值抽成階梯
                  </Button>
                </>
              )}
            </Form.List>

            <Actions>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                清除
              </Button>
              <Button
                type='primary'
                icon={<SaveOutlined />}
                onClick={handleSubmit}
                loading={saving}
                disabled={loading}
              >
                保存設定
              </Button>
            </Actions>
          </FormWrapper>

          {loading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 16
              }}
            >
              <Spin size='small' tip='載入儲值抽成設定中...' />
            </div>
          )}
        </StyledCard>

        <InfoCard>
          <Title level={5} style={{ color: '#0f2562', marginBottom: 12 }}>
            設定提醒
          </Title>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>
              • 建議從保底門檻開始設定，例如 0 元、100,000 元、200,000 元等。
            </Text>
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>
              • 抽成比例可依照儲值商品毛利率調整，鼓勵業績成長。
            </Text>
          </Paragraph>
          <Paragraph>
          </Paragraph>
        </InfoCard>
      </ContentWrapper>
    </PageWrapper>
  )
}

export default ValueAddedCommissionSettingsPage
